// Settings → Delivery Addresses tab.
//
// Manages a labelled list of delivery addresses (Showroom, Warehouse, Cabinet
// manufacturer, Storage facility, …) that the Place Order / PO flow offers as
// "deliver to" options. One address is the primary (default selection on a PO).
//
// Backend: GET/POST /api/portal/settings/delivery-addresses
// (modules/portal_retailer_profile.js → retailers.delivery_addresses jsonb,
// migration 0058). No Stripe / commission / defraud interaction.

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import type { SettingsTab, TabCleanup } from "../types";
import type { PageContext } from "../../../shell/types";

import "./delivery-addresses.css";

interface DeliveryAddress {
  id: string;
  label: string;
  line1: string;
  line2: string;
  city: string;
  county: string;
  postcode: string;
  country: string;
  isPrimary: boolean;
}

function freshId(): string {
  return `da-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function emptyAddr(): DeliveryAddress {
  return { id: freshId(), label: "", line1: "", line2: "", city: "", county: "", postcode: "", country: "", isPrimary: false };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function coerce(a: Partial<DeliveryAddress>): DeliveryAddress {
  return {
    id: typeof a.id === "string" && a.id ? a.id : freshId(),
    label: String(a.label ?? "").slice(0, 80),
    line1: String(a.line1 ?? "").slice(0, 200),
    line2: String(a.line2 ?? "").slice(0, 200),
    city: String(a.city ?? "").slice(0, 120),
    county: String(a.county ?? "").slice(0, 120),
    postcode: String(a.postcode ?? "").slice(0, 20),
    country: String(a.country ?? "").slice(0, 80),
    isPrimary: !!a.isPrimary,
  };
}

const F: Array<{ key: keyof DeliveryAddress; label: string; placeholder: string; wide?: boolean }> = [
  { key: "label", label: "Label", placeholder: "e.g. Warehouse, Showroom, Cabinet manufacturer", wide: true },
  { key: "line1", label: "Address line 1", placeholder: "Unit / street", wide: true },
  { key: "line2", label: "Address line 2", placeholder: "(optional)", wide: true },
  { key: "city", label: "Town / city", placeholder: "" },
  { key: "county", label: "County", placeholder: "" },
  { key: "postcode", label: "Postcode", placeholder: "" },
];

function cardHtml(a: DeliveryAddress): string {
  const fields = F.map((f) => `
    <label class="set-da-field${f.wide ? " set-da-field--wide" : ""}">
      <span>${escapeHtml(f.label)}</span>
      <input type="text" data-f="${f.key}" value="${escapeHtml(String(a[f.key] ?? ""))}" placeholder="${escapeHtml(f.placeholder)}" />
    </label>`).join("");
  return `
    <article class="set-da-card" data-id="${escapeHtml(a.id)}">
      <header class="set-da-card__head">
        <label class="set-da-primary">
          <input type="radio" name="set-da-primary" data-act="primary" ${a.isPrimary ? "checked" : ""} />
          <span>${a.isPrimary ? "Primary (default on POs)" : "Set as primary"}</span>
        </label>
        <button type="button" class="set-da-remove" data-act="remove" aria-label="Remove address">${iconMarkup("trash", { size: "sm" })}</button>
      </header>
      <div class="set-da-grid">${fields}</div>
    </article>`;
}

const tab: SettingsTab = {
  id: "delivery-addresses",
  label: "Delivery Addresses",
  icon: iconMarkup("truck", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    let state: DeliveryAddress[] = [];
    const controller = new AbortController();
    let disposed = false;

    container.innerHTML = `
      <section class="set-da-root">
        <header class="set-da-header">
          <h2 class="set-section-title">Delivery Addresses</h2>
          <p class="set-help">Saved addresses you can deliver supplier orders to — your showroom, warehouse, a cabinet manufacturer, a storage facility, and so on. These appear as options when you raise a purchase order. The primary one is selected by default.</p>
        </header>
        <div class="set-da-list" data-role="list"></div>
        <div class="set-da-actions">
          <button type="button" class="set-btn set-btn-secondary" data-role="add">+ Add address</button>
          <button type="button" class="set-btn" data-role="save">Save addresses</button>
        </div>
      </section>`;

    const listEl = container.querySelector<HTMLElement>("[data-role='list']")!;
    const addBtn = container.querySelector<HTMLButtonElement>("[data-role='add']")!;
    const saveBtn = container.querySelector<HTMLButtonElement>("[data-role='save']")!;

    const render = (): void => {
      listEl.innerHTML = state.length
        ? state.map(cardHtml).join("")
        : `<p class="set-da-empty">No delivery addresses yet. Add your showroom, warehouse, or any address you take supplier deliveries to.</p>`;
    };

    const onInput = (e: Event): void => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      const card = t.closest<HTMLElement>(".set-da-card");
      const id = card?.getAttribute("data-id");
      const field = t.getAttribute("data-f");
      if (!id || !field) return;
      const addr = state.find((a) => a.id === id);
      if (!addr) return;
      (addr as unknown as Record<string, string>)[field] = t.value;
    };

    const onClick = (e: Event): void => {
      const el = (e.target as HTMLElement).closest<HTMLElement>("[data-act]");
      if (!el) return;
      const card = el.closest<HTMLElement>(".set-da-card");
      const id = card?.getAttribute("data-id");
      if (!id) return;
      const act = el.getAttribute("data-act");
      if (act === "remove") {
        const wasPrimary = state.find((a) => a.id === id)?.isPrimary;
        state = state.filter((a) => a.id !== id);
        if (wasPrimary && state.length) state[0]!.isPrimary = true;
        render();
      } else if (act === "primary") {
        for (const a of state) a.isPrimary = a.id === id;
        render();
      }
    };

    const onAdd = (): void => {
      const a = emptyAddr();
      if (state.length === 0) a.isPrimary = true;
      state = [...state, a];
      render();
      listEl.querySelector<HTMLInputElement>(`.set-da-card[data-id='${a.id}'] input[data-f='label']`)?.focus();
    };

    const onSave = async (): Promise<void> => {
      // Drop blank rows (no label and no line1).
      const clean = state.filter((a) => a.label.trim() || a.line1.trim());
      if (clean.length && !clean.some((a) => a.isPrimary)) clean[0]!.isPrimary = true;
      saveBtn.disabled = true;
      const orig = saveBtn.textContent;
      saveBtn.textContent = "Saving…";
      try {
        const res = await ctx.api<{ ok?: boolean; addresses?: DeliveryAddress[] }>(
          "/portal/settings/delivery-addresses",
          { method: "POST", body: JSON.stringify({ addresses: clean }), signal: controller.signal },
        );
        if (!res.ok) throw new Error("save failed");
        state = (res.addresses ?? clean).map(coerce);
        render();
        ctx.showToast("Delivery addresses saved", "success");
      } catch (err) {
        if (controller.signal.aborted) return;
        const msg = err instanceof ApiError ? `Couldn't save (HTTP ${err.status})` : "Couldn't save addresses";
        ctx.showToast(msg, "error");
      } finally {
        if (!disposed) { saveBtn.disabled = false; saveBtn.textContent = orig; }
      }
    };

    listEl.addEventListener("input", onInput);
    listEl.addEventListener("click", onClick);
    addBtn.addEventListener("click", onAdd);
    saveBtn.addEventListener("click", () => void onSave());

    render();
    void (async () => {
      try {
        const res = await ctx.api<{ addresses?: Array<Partial<DeliveryAddress>> }>(
          "/portal/settings/delivery-addresses",
          { signal: controller.signal },
        );
        if (disposed || controller.signal.aborted) return;
        if (Array.isArray(res.addresses)) {
          state = res.addresses.map(coerce);
          render();
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.warn("[settings/delivery-addresses] load failed:", err);
      }
    })();

    return (): void => {
      disposed = true;
      controller.abort();
      listEl.removeEventListener("input", onInput);
      listEl.removeEventListener("click", onClick);
    };
  },
};

export default tab;
